import { ref } from "vue";
import { Loader } from "@allwyn/design-system/src/loader/loader";
import { triggerMobileAppEvent } from "@allwyn/mobile-apps/mobile-app-integration";
import { useNg3DsRedirection } from "@allwyn/ng/ng-3ds-redirection";
import { ngFrameworkLoaded } from "@allwyn/ng/ng-integration";
import { useNgSession } from "@allwyn/ng/ng-session";
import { confirmAge } from "@allwyn/shared-components/age-confirmation/age-confirmation";
import { getAppDeepLink } from "@allwyn/utils/app-deep-link";
import { httpClient } from "@allwyn/utils/http-clients/http-client";
import { logCustomException } from "@allwyn/utils/logger/use-app-logger";
import { UrlUtils } from "@allwyn/utils/url";
import { useCasinoCheckoutManager } from "./casino-checkout-manager";
import { NgCasinoAnalytics } from "./ng-casino-analytics";
import { handleFullscreenCapture } from "./ng-casino-fullscreen";
import { useNgCasinoUtils } from "./use-ng-casino-utils";
import { CasinoGameLoadParams } from "../model/casino-game-load-params";

const { constructGameLaunchUrl, getAppCasinoDeepLink, handleCloseNgGame } = useNgCasinoUtils();

export type GameClickAnalyticsCallback = (params: {
    placement: string;
    position: number;
    gameCode: string | null;
    gameMode: "money" | "fun";
    supplier: string | null;
}) => void;

function getPlacementGames(wrapper: HTMLElement, placement: string) {
    const placementWrappers = Array.from(
        wrapper.parentElement?.querySelectorAll(`[data-a-games-placement="${placement}"]`) ?? []
    );

    if (placementWrappers.length === 0) {
        return Array.from(wrapper.querySelectorAll(".game-launcher"));
    }

    return placementWrappers.flatMap((placementWrapper) =>
        Array.from(placementWrapper.querySelectorAll(".game-launcher"))
    );
}

let casinoModule: typeof import("./ng-casino-module");
const { isLoggedIn } = useNgSession();
const isNgCasinoModuleLoaded = ref(false);

export const useNgCasino = () => {
    return {
        isNgCasinoModuleLoaded,
        initializeNgCasino,
        launchGame,
        launchTransparentGame,
        closeGame,
        closeTransparentGame,
    };
};

async function initializeNgCasino() {
    registerGameClick();

    await ngFrameworkLoaded();

    startCasinoGameFromQuery();
    registerNgHsCallbacks();
}

async function launchTransparentGame(gameLoadParams: CasinoGameLoadParams) {
    const casinoModule = await loadCasinoModule();
    const { loadTransparentGame, initialize } = casinoModule.useNgCasinoModule();

    await initialize();
    await loadTransparentGame(gameLoadParams);
}

async function launchGame(gameLoadParams: CasinoGameLoadParams) {
    if (environment?.webView?.isAppWebView) {
        const handled = await handleMobileAppGameLaunch(gameLoadParams, isLoggedIn.value);
        if (handled) {
            return;
        }
    } else {
        try {
            await confirmAge();
        } catch {
            // user did not confirm age, do not launch the game
            return;
        }
    }

    const launchUrl = constructGameLaunchUrl(gameLoadParams);

    if (!launchUrl) {
        const errorMsg = "Cannot construct game launch URL - missing gameId or gameUrl";

        logCustomException("use-ng-casino-game-launch", errorMsg, { gameLoadParams });
        console.error(errorMsg);
        return;
    }
    window.location.href = launchUrl;
}

function closeGame() {
    handleCloseNgGame();
    ng_fw.closeGame();
}

function closeTransparentGame() {
    ng_fw.closeGame();
}

async function loadCasinoModule() {
    if (!casinoModule) {
        try {
            Loader.show();
            casinoModule = await import(/* webpackPrefetch: true */ "./ng-casino-module");
            isNgCasinoModuleLoaded.value = true;

            handleFullscreenCapture(casinoModule);
        } catch (e) {
            console.error(e);
        } finally {
            Loader.hide();
        }
    }

    return casinoModule;
}

function startCasinoGameFromQuery() {
    const id = UrlUtils.getQueryString("game");

    if (id) {
        launchGame({
            gameId: Number(id),
        });
    }
}

function parseMaybeBooleanAttribute(value: string | boolean | null): boolean | undefined {
    if (value?.toString().toLowerCase() === "true") return true;
    if (value?.toString().toLowerCase() === "false") return false;
    return undefined;
}

function parseBooleanAttribute(value: string | boolean | null): boolean {
    return value?.toString().toLowerCase() === "true";
}

async function handleMobileAppGameLaunch(gameLoadParams: CasinoGameLoadParams, isLoggedIn: boolean): Promise<boolean> {
    if (gameLoadParams.moneyMode === true && !isLoggedIn) {
        triggerMobileAppEvent("goToDeepLink", getAppDeepLink("login"));
        return true;
    }

    if (typeof gameLoadParams.moneyMode !== "boolean") {
        gameLoadParams.moneyMode = isLoggedIn;
    }

    const deepLink = await getAppCasinoDeepLink(gameLoadParams);
    if (!deepLink) {
        return false;
    }

    triggerMobileAppEvent("goToDeepLink", deepLink);
    return true;
}

async function countFunparkGame() {
    if (!isLoggedIn.value) {
        return;
    }

    const nodeAliasPath = environment.nodeAliasPath?.toLowerCase();
    if (nodeAliasPath === "/funpark" || nodeAliasPath === "/funpark/katalog") {
        try {
            await httpClient.put(`/api/funpark/count-game?nodeAliasPath=${encodeURIComponent(nodeAliasPath)}`, null, {
                fetchOptions: { keepalive: true },
            });
        } catch {
            // non critical code, fail silently
        }
    }
}

function parseGameLoadParams(gameLauncher: HTMLAnchorElement): CasinoGameLoadParams {
    const gameUrl = gameLauncher.href;
    const gameId = Number(gameLauncher.getAttribute("data-game-id"));
    const moneyMode = parseMaybeBooleanAttribute(gameLauncher.getAttribute("data-money-mode"));
    const withAbTest = parseMaybeBooleanAttribute(gameLauncher.getAttribute("data-ab-test")) ?? true;

    return {
        gameUrl,
        gameId,
        moneyMode,
        withAbTest,
    };
}

function getAnalyticsPlacement(gameLauncher: HTMLAnchorElement) {
    let position = 0;
    const wrapper = gameLauncher.closest("[data-a-games-placement]");
    let placement = wrapper?.getAttribute("data-a-games-placement") || null;

    if (placement && wrapper instanceof HTMLElement) {
        const gamesInPlacement = getPlacementGames(wrapper, placement);
        const filteredGames = gamesInPlacement.filter((g) => {
            const el = g as HTMLElement;
            return el.style.display !== "none" && el.parentElement?.style.display !== "none";
        });

        position = filteredGames.indexOf(gameLauncher) + 1;

        const feedSection = wrapper.closest<HTMLElement>("[data-a-feed-section]");
        const isNumberedPlacement = (wrapper as HTMLElement)?.dataset.aNumberedPlacement;

        if (feedSection) {
            const sectionName = feedSection.dataset.aFeedSection;
            const allNumbered = document.querySelectorAll("[data-a-numbered-placement]");
            const sectionIndex = Array.from(allNumbered).indexOf(feedSection) + 1;

            placement = `${sectionIndex}.${sectionName}.${placement}`;
        } else if (isNumberedPlacement) {
            const placementWrappers = Array.from(document.querySelectorAll<HTMLElement>("[data-cms-widget]"));
            const numberedPlacements = placementWrappers.map((d) => d.dataset.aGamesPlacement);
            const placementIndex = numberedPlacements.indexOf(placement);
            if (placementIndex > -1) {
                placement = `${placementIndex + 1}.${placement}`;
            }
        }

        return {
            placement,
            position,
        };
    }

    return {
        placement: null,
        position: 0,
    };
}

function registerGameClick() {
    document.addEventListener("click", async (e) => {
        const clickedElement = e.target as Element;
        const gameLauncher = clickedElement.closest(".game-launcher") as HTMLAnchorElement;
        if (!gameLauncher) return;

        e.preventDefault();

        countFunparkGame(); // we shouldn't need to await this to not delay game launch - use keepalive fetch option instead

        const gameLoadParams = parseGameLoadParams(gameLauncher);

        const { placement, position } = getAnalyticsPlacement(gameLauncher);

        const moneyMode = gameLoadParams.moneyMode ?? isLoggedIn.value;
        NgCasinoAnalytics.pushGameDetail({
            analyticsGameCode: gameLauncher.getAttribute("data-game-code") ?? "",
            moneyMode,
            gameId: gameLoadParams.gameId,
            placement: placement ?? "",
            position,
            url: gameLoadParams.gameUrl ?? "",
        });

        const isTransparent = parseBooleanAttribute(gameLauncher.getAttribute("data-transparent"));
        if (isTransparent && !environment?.webView?.isAppWebView) {
            launchTransparentGame(gameLoadParams);
            return;
        }

        launchGame(gameLoadParams);
    });
}

async function registerNgHsCallbacks() {
    ng_hs.instantGame.gameLoadStart((gameId: number, playMode: "M" | "D") => {
        loadCasinoModule().then(async (module) => {
            const casinoModule = module.useNgCasinoModule();

            await casinoModule.initialize();
            casinoModule.ngGameReady(gameId, playMode);
        });
    });

    useNg3DsRedirection().beforeRedirection(async () => {
        await (await useCasinoCheckoutManager()).onDepositRedirectionStart();
    });

    (await useCasinoCheckoutManager()).registerCheckoutCallbacks();
}
